import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import Icon from '../../components/AppIcon';
import { useLanguage } from '../../contexts/LanguageContext';

const UpdatePassword = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [sessionReady, setSessionReady] = useState(false);

  useEffect(() => {
    // Supabase fires an AUTH_CHANGE event with type SIGNED_IN when the
    // recovery token in the URL is exchanged for a session.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY' || event === 'SIGNED_IN') {
        setSessionReady(true);
      }
    });

    // Also check if there's already an active session (token already exchanged)
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) setSessionReady(true);
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError(t?.login?.passwordMismatch || 'Passwords do not match.');
      return;
    }
    if (password.length < 6) {
      setError(t?.login?.passwordTooShort || 'Password must be at least 6 characters.');
      return;
    }

    setLoading(true);
    try {
      const { error: updateError } = await supabase.auth.updateUser({ password });
      if (updateError) {
        setError(updateError.message);
      } else {
        setSuccess(true);
        setTimeout(() => navigate('/login'), 3000);
      }
    } catch {
      setError(t?.login?.networkError || 'Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const inputClass = "premium-input";

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left decorative panel */}
      <div
        className="hidden lg:flex lg:w-[45%] xl:w-[50%] relative overflow-hidden flex-col justify-between p-12"
        style={{ background: 'linear-gradient(160deg, #1B2D5B 0%, #0F1A35 50%, #0A1020 100%)' }}
      >
        <div className="absolute top-0 right-0 w-96 h-96 rounded-full opacity-10"
          style={{ background: 'radial-gradient(circle, #B8963E 0%, transparent 70%)', transform: 'translate(30%, -30%)' }} />
        <div className="absolute bottom-0 left-0 w-80 h-80 rounded-full opacity-8"
          style={{ background: 'radial-gradient(circle, #4A7FD4 0%, transparent 70%)', transform: 'translate(-30%, 30%)' }} />
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-16">
            <img
              src="/assets/images/logo_USenghor_vertical_blanc_1000_min-1774525392061.png"
              alt="Université Senghor logo"
              className="w-12 h-12 object-contain"
            />
            <div>
              <div className="text-white font-bold text-sm" style={{ fontFamily: 'Manrope, sans-serif' }}>US Réservation</div>
              <div className="text-white/40 text-xs">Résidences</div>
            </div>
          </div>
          <h2 className="text-4xl font-bold text-white leading-tight mb-4" style={{ fontFamily: 'Manrope, sans-serif', letterSpacing: '-0.03em' }}>
            {t?.login?.premiumResidence || 'Premium Residence'}<br />{t?.login?.management || 'Management'}
          </h2>
          <p className="text-white/50 text-base leading-relaxed max-w-sm">
            {t?.login?.streamlineOps || 'Streamline your operations with our powerful management system.'}
          </p>
        </div>
      </div>

      {/* Right — form panel */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-[400px]">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <img
              src="/assets/images/logo_USenghor_vertical_blanc_1000_min-1774525392061.png"
              alt="Université Senghor logo"
              className="w-10 h-10 object-contain"
            />
            <div>
              <div className="font-bold text-foreground text-sm" style={{ fontFamily: 'Manrope, sans-serif' }}>US Réservation Résidences</div>
            </div>
          </div>

          <div className="mb-7">
            <h1 className="text-2xl font-bold text-foreground" style={{ fontFamily: 'Manrope, sans-serif', letterSpacing: '-0.02em' }}>
              {t?.login?.resetPassword || 'Reset Password'}
            </h1>
            <p className="text-muted-foreground text-sm mt-1">
              {t?.login?.newPasswordDesc || 'Enter your new password below.'}
            </p>
          </div>

          {success ? (
            <div className="text-center py-6">
              <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4"
                style={{ background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.2)' }}>
                <Icon name="CheckCircle" size={26} className="text-green-500" />
              </div>
              <p className="text-foreground font-semibold mb-1">
                {t?.login?.passwordUpdated || 'Password updated successfully!'}
              </p>
              <p className="text-muted-foreground text-sm">
                {t?.login?.redirectingToLogin || 'Redirecting you to the login page…'}
              </p>
            </div>
          ) : (
            <>
              {error && (
                <div className="mb-5 p-3 rounded-lg flex items-start gap-2.5"
                  style={{ background: 'rgba(192,57,43,0.08)', border: '1px solid rgba(192,57,43,0.2)' }}>
                  <Icon name="AlertCircle" size={15} className="text-error mt-0.5 flex-shrink-0" />
                  <p className="text-error text-sm">{error}</p>
                </div>
              )}

              {!sessionReady ? (
                <div className="text-center py-8">
                  <Icon name="Loader2" size={28} className="animate-spin text-muted-foreground mx-auto mb-3" />
                  <p className="text-muted-foreground text-sm">
                    {t?.login?.verifyingLink || 'Verifying reset link…'}
                  </p>
                </div>
              ) : (
                <form onSubmit={handleUpdatePassword} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5">
                      {t?.login?.newPassword || 'New Password'}
                    </label>
                    <input
                      type="password"
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5">
                      {t?.login?.confirmPassword || 'Confirm Password'}
                    </label>
                    <input
                      type="password"
                      required
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="••••••••"
                      className={inputClass}
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 mt-2"
                    style={{ background: 'var(--color-primary)', color: '#fff' }}
                  >
                    {loading
                      ? <><Icon name="Loader2" size={16} className="animate-spin" /> {t?.login?.updating || 'Updating…'}</>
                      : (t?.login?.updatePassword || 'Update Password')
                    }
                  </button>
                  <div className="text-center mt-1">
                    <button
                      type="button"
                      onClick={() => navigate('/login')}
                      className="text-sm text-muted-foreground hover:text-foreground transition-colors underline-offset-2 hover:underline flex items-center gap-1.5 mx-auto"
                    >
                      <Icon name="ArrowLeft" size={14} />
                      {t?.login?.backToSignIn || 'Back to Sign In'}
                    </button>
                  </div>
                </form>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default UpdatePassword;
